/**
 * AudioPlayer — player flutuante do LeadTalk/LeadMusic com fila (protótipo "Vitrine + player
 * flutuante com fila", 22/10/2026). Toca pelo iframe oculto da YouTube IFrame API, um player só:
 * a troca de item usa loadVideoById, que mantém a permissão de tocar dada pelo clique. A fila
 * vive no playerStore. Cheio (capa, progresso, controles, fila) ou mini (uma linha); arrasta pela
 * faixa de cima ou pelas setas no botão "Mover". Fica acima de tudo (z-index em audioPlayer.css).
 *
 * Movimento (23/10/2026): o player entra com .ds-entra (200 ms, sobe 8 px); tocar ↔ pausar (e
 * "tocar a fila de novo" no fim) troca o ícone no lugar com .ds-troca (140 ms); os botões
 * respondem só com a cor (.ds-resposta, M1 a). Percorrendo os controles, cada dica vem sem nova
 * espera (useDicaSemEspera).
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { emitActivity } from '../../lib/activity';
import { seedLeadTalk, updateLeadTalkTime } from '../../api/useLeadTalk';
import { useAmbassadorStore } from '../../features/ambassador/ambassadorStore';
import {
  PLAYER_SPEEDS,
  currentTrack,
  hasPrev,
  upNext,
  usePlayerStore,
} from '../../features/player/playerStore';
import { formatClock, formatDuration } from '../../features/player/duration';
import { useDicaSemEspera } from '../../features/contents/dicaSemEspera';
import './audioPlayer.css';

/** API mínima do player do YouTube (IFrame API) usada aqui. */
interface YTPlayer {
  playVideo?: () => void;
  pauseVideo?: () => void;
  stopVideo?: () => void;
  destroy?: () => void;
  cueVideoById?: (id: string) => void;
  loadVideoById?: (id: string) => void;
  getCurrentTime?: () => number;
  getDuration?: () => number;
  seekTo?: (seconds: number, allowSeekAhead: boolean) => void;
  setPlaybackRate?: (rate: number) => void;
  getPlaybackRate?: () => number;
}

interface YTNamespace {
  Player: new (elementId: string, config: unknown) => YTPlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: (() => void) | null;
  }
}

/** Distância das bordas da janela; em cima, abaixo da barra do painel (60 px). */
const EDGE = 8;
const TOP = 68;
const NUDGE = 24;

function clampPlace(x: number, y: number, w: number, h: number): { x: number; y: number } {
  return {
    x: Math.max(EDGE, Math.min(window.innerWidth - w - EDGE, x)),
    y: Math.max(TOP, Math.min(window.innerHeight - h - EDGE, y)),
  };
}

export function AudioPlayer(): JSX.Element | null {
  const { t } = useTranslation();
  const store = usePlayerStore();
  const openShare = useAmbassadorStore((s) => s.openShare);
  const item = currentTrack(store);
  const talk = store.source === 'leadtalk';
  const dicas = useDicaSemEspera();

  const sectionRef = useRef<HTMLElement | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const dragRef = useRef<{ cx: number; cy: number; x0: number; y0: number } | null>(null);
  /** Entre o loadVideoById e o vídeo começar a tocar. */
  const loadingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [mini, setMini] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [place, setPlace] = useState<{ x: number; y: number } | null>(null);

  const rate = talk ? (PLAYER_SPEEDS[store.speed] ?? 1) : 1;
  const rateRef = useRef(rate);

  // Um player do YouTube para a vida toda do player flutuante (script carregado uma vez).
  useEffect(() => {
    let cancelled = false;
    const onState = (state: number): void => {
      const YT = window.YT;
      const p = playerRef.current;
      const s = usePlayerStore.getState();
      if (!YT || !p) return;
      if (state === YT.PlayerState.PLAYING) {
        loadingRef.current = false;
        s.setPlaying(true);
        if (p.getPlaybackRate?.() !== rateRef.current) p.setPlaybackRate?.(rateRef.current);
      } else if (state === YT.PlayerState.PAUSED) {
        // Ao carregar outro vídeo, o YouTube avisa "pausado" antes de começar: não é a pessoa.
        if (!loadingRef.current) s.setPlaying(false);
      } else if (state === YT.PlayerState.ENDED) {
        if (s.repeat === 'one') {
          p.seekTo?.(0, true);
          p.playVideo?.();
        } else {
          s.next(true);
        }
      }
    };
    const mount = (): void => {
      if (cancelled || !window.YT?.Player) return;
      playerRef.current = new window.YT.Player('ytplayer', {
        height: '1',
        width: '1',
        playerVars: { autoplay: 0, controls: 0, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => {
            if (!cancelled) setReady(true);
          },
          onStateChange: (e: { data: number }) => onState(e.data),
        },
      });
    };
    if (window.YT?.Player) {
      mount();
    } else {
      window.onYouTubeIframeAPIReady = mount;
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
    }
    // Progresso e telemetria de escuta (legado: this.current, em segundos).
    const timer = setInterval(() => {
      const p = playerRef.current;
      if (!p?.getCurrentTime) return;
      const cur = p.getCurrentTime() ?? 0;
      const d = p.getDuration?.() ?? 0;
      setPos(cur);
      if (d > 0) setDur(d);
      const it = currentTrack(usePlayerStore.getState());
      if (it && cur > 0) void updateLeadTalkTime(it.id, Math.ceil(cur));
    }, 500);
    return () => {
      cancelled = true;
      window.onYouTubeIframeAPIReady = null;
      clearInterval(timer);
      try {
        playerRef.current?.stopVideo?.();
        playerRef.current?.destroy?.();
      } catch {
        // player já destruído
      }
      playerRef.current = null;
    };
  }, []);

  // Troca de item: carrega (tocando) ou só deixa pronto (pausado). Semeia a telemetria.
  const itemId = item?.id;
  useEffect(() => {
    const p = playerRef.current;
    const it = currentTrack(usePlayerStore.getState());
    if (!ready || !p || !it) return;
    const format = usePlayerStore.getState().source === 'leadmusic' ? 'leadmusic' : 'leadtalk';
    emitActivity('content.played', { format }, { item: it.id });
    void seedLeadTalk(it.id, it.title, usePlayerStore.getState().source ?? 'leadtalk');
    setPos(0);
    setDur(it.duration ?? 0);
    loadingRef.current = usePlayerStore.getState().playing;
    if (loadingRef.current) p.loadVideoById?.(it.id);
    else p.cueVideoById?.(it.id);
  }, [ready, itemId]);

  // Tocar/pausar vindo da fila (botões e cartões da vitrine).
  useEffect(() => {
    const p = playerRef.current;
    if (!ready || !p) return;
    if (store.playing) {
      p.playVideo?.();
    } else {
      loadingRef.current = false;
      p.pauseVideo?.();
    }
  }, [ready, store.playing]);

  // Velocidade do LeadTalk; o YouTube pode voltar a 1x ao carregar outro vídeo (ver onState).
  useEffect(() => {
    rateRef.current = rate;
    if (ready) playerRef.current?.setPlaybackRate?.(rate);
  }, [ready, rate]);

  // Mudou de tamanho (mini, fila aberta) ou a janela: o player continua inteiro na tela.
  useLayoutEffect(() => {
    const keepInside = (): void => {
      const el = sectionRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPlace((cur) => (cur ? clampPlace(cur.x, cur.y, r.width, r.height) : cur));
    };
    keepInside();
    window.addEventListener('resize', keepInside);
    return () => window.removeEventListener('resize', keepInside);
  }, [mini, queueOpen]);

  if (!item) return null;

  const k = store.order.indexOf(store.cur);
  // Tudo o que falta tocar (com "repetir tudo", todos os outros); a lista da fila rola.
  const ups = upNext(store, store.order.length);
  const nextIndex = ups[0];
  const next = nextIndex !== undefined ? store.ctx?.tracks[nextIndex] : undefined;
  const total = dur || item.duration || 0;
  const pct = total > 0 ? Math.min(100, (pos / total) * 100) : 0;
  const playLabel = store.ended
    ? t('player_replay_queue', 'Tocar a fila de novo')
    : store.playing
      ? t('player_pause', 'Pausar')
      : t('player_play', 'Tocar');
  const playIcon = store.ended ? 'replay' : store.playing ? 'pause' : 'play_arrow';
  // Os três ícones empilhados (.ds-troca): o de agora à vista, os outros de fora.
  const playIcons = ['play_arrow', 'pause', 'replay'] as const;
  const prevLabel = talk ? t('player_prev_talk', 'Episódio anterior') : t('player_prev_music', 'Música anterior');
  const nextLabel = talk ? t('player_next_talk', 'Próximo episódio') : t('player_next_music', 'Próxima música');
  const repeatLabel = {
    off: t('player_repeat_off', 'Repetir: desligado'),
    all: t('player_repeat_all', 'Repetir: toda a fila'),
    one: t('player_repeat_one', 'Repetir: só esta'),
  }[store.repeat];
  const status = store.ended
    ? t('player_queue_end', 'Fim da fila')
    : store.repeat === 'one'
      ? talk
        ? t('player_repeating_talk', 'Repetindo este episódio')
        : t('player_repeating_music', 'Repetindo esta música')
      : next
        ? t('player_up_next', 'A seguir: {{title}}', { title: next.title })
        : t('player_queue_last', 'Última da fila');
  const speedText = `${String(rate).replace('.', ',')}x`;
  const style: CSSProperties = place ? { left: place.x, top: place.y } : { right: 24, bottom: 24 };

  function seek(seconds: number): void {
    const target = Math.max(0, total > 0 ? Math.min(total - 1, seconds) : seconds);
    playerRef.current?.seekTo?.(target, true);
    setPos(target);
  }

  function prev(): void {
    // Passou do começo: "anterior" volta ao início do item, como nos players de música.
    if ((playerRef.current?.getCurrentTime?.() ?? pos) > 3 || !hasPrev(usePlayerStore.getState())) seek(0);
    else store.prev();
  }

  function share(): void {
    if (!store.source || !item) return;
    openShare({ kind: 'content', format: store.source, videoId: item.id, title: item.title });
  }

  function startDrag(e: PointerEvent<HTMLDivElement>): void {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('button') && !target.closest('[data-move]')) return;
    const el = sectionRef.current;
    if (!el) return;
    // O arrasto cancela o foco do clique; o "Mover" fica focado para as setas continuarem.
    target.closest<HTMLElement>('[data-move]')?.focus();
    const r = el.getBoundingClientRect();
    dragRef.current = { cx: e.clientX, cy: e.clientY, x0: r.left, y0: r.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setPlace({ x: r.left, y: r.top });
    e.preventDefault();
  }

  function moveDrag(e: PointerEvent<HTMLDivElement>): void {
    const d = dragRef.current;
    const el = sectionRef.current;
    if (!d || !el) return;
    const r = el.getBoundingClientRect();
    setPlace(clampPlace(d.x0 + e.clientX - d.cx, d.y0 + e.clientY - d.cy, r.width, r.height));
  }

  function endDrag(): void {
    dragRef.current = null;
  }

  function nudge(e: KeyboardEvent<HTMLButtonElement>): void {
    const steps: Record<string, [number, number]> = {
      ArrowLeft: [-NUDGE, 0],
      ArrowRight: [NUDGE, 0],
      ArrowUp: [0, -NUDGE],
      ArrowDown: [0, NUDGE],
    };
    const step = steps[e.key];
    const el = sectionRef.current;
    if (!step || !el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    setPlace(clampPlace(r.left + step[0], r.top + step[1], r.width, r.height));
  }

  const dragProps = {
    className: 'ap-grab',
    onPointerDown: startDrag,
    onPointerMove: moveDrag,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };

  const moveButton = (
    <button
      type="button"
      className="ap-move"
      data-move=""
      aria-label={t('player_move', 'Mover o player (use as setas)')}
      onKeyDown={nudge}
    >
      <span className="material-symbols-rounded" aria-hidden="true">
        drag_indicator
      </span>
    </button>
  );

  const playButton = (
    <button type="button" className="ap-play ds-resposta" aria-label={playLabel} onClick={store.toggle}>
      <span className="ds-troca" aria-hidden="true">
        {playIcons.map((icon) => (
          <span
            key={icon}
            className={`material-symbols-rounded ap-fill${icon === playIcon ? '' : ' ds-fora'}`}
          >
            {icon}
          </span>
        ))}
      </span>
    </button>
  );

  const nextButton = (
    <button
      type="button"
      className="ap-icon ap-icon--step ds-resposta"
      aria-label={nextLabel}
      data-dica={nextLabel}
      aria-disabled={next ? undefined : 'true'}
      onClick={() => {
        if (next) store.next();
      }}
    >
      <span className="material-symbols-rounded ap-fill" aria-hidden="true">
        skip_next
      </span>
    </button>
  );

  return createPortal(
    <section
      ref={sectionRef}
      id="player"
      className={`ap ds-entra${mini ? ' ap--mini' : ''}`}
      role="region"
      aria-label={t('player_region', 'Player')}
      style={style}
      {...dicas}
    >
      <div id="ytplayer" />
      {mini ? (
        <div {...dragProps} className="ap-grab ap-mini">
          {moveButton}
          {item.img ? <img className="ap-mini__img" src={item.img} alt="" /> : null}
          <div className="ap-mini__text">
            <div className="ap-ell ap-mini__title">{item.title}</div>
            <div className="ap-mini__bar" aria-hidden="true">
              <div style={{ width: `${pct}%` }} />
            </div>
          </div>
          {playButton}
          {nextButton}
          <button
            type="button"
            className="ap-icon ds-resposta"
            aria-label={t('player_expand', 'Abrir player')}
            data-dica={t('player_expand', 'Abrir player')}
            onClick={() => setMini(false)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              open_in_full
            </span>
          </button>
        </div>
      ) : (
        <>
          <div {...dragProps}>
            {moveButton}
            <span className="ap-ell ap-from">
              {t('player_playing_from', 'Tocando de {{name}}', { name: store.ctx?.name ?? '' })}
            </span>
            <button
              type="button"
              className="ap-icon ds-resposta"
              aria-label={t('player_minimize', 'Minimizar player')}
              data-dica={t('player_minimize', 'Minimizar player')}
              onClick={() => setMini(true)}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                close_fullscreen
              </span>
            </button>
            <button
              type="button"
              className="ap-icon ds-resposta"
              aria-label={t('player_close', 'Fechar player')}
              data-dica={t('player_close', 'Fechar player')}
              onClick={store.close}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                close
              </span>
            </button>
          </div>

          <div className="ap-body">
            <div className="ap-now">
              {item.img ? <img src={item.img} alt="" /> : null}
              <div className="ap-now__text">
                <div className="ap-clamp2 ap-now__title">{item.title}</div>
                <div className="ap-ell ap-now__sub">
                  {t('player_position', '{{n}} de {{total}}', { n: k + 1, total: store.order.length })} · {item.sub}
                </div>
              </div>
            </div>

            <input
              className="ap-range"
              type="range"
              min="0"
              max={Math.max(1, Math.floor(total))}
              step="1"
              value={Math.floor(pos)}
              aria-label={t('player_progress', 'Posição do áudio')}
              aria-valuetext={`${formatClock(pos)} de ${formatClock(total)}`}
              style={{ '--pct': `${pct}%` } as CSSProperties}
              onChange={(e) => seek(Number(e.target.value))}
            />
            <div className="ap-times">
              <span>{formatClock(pos)}</span>
              <span>{formatClock(total)}</span>
            </div>

            <div className="ap-controls">
              {talk ? (
                <button
                  type="button"
                  className="ap-icon ds-resposta"
                  aria-label={t('player_back10', 'Voltar 10 segundos')}
                  data-dica={t('player_back10', 'Voltar 10 segundos')}
                  onClick={() => seek(pos - 10)}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    replay_10
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="ap-icon ap-toggle ds-resposta"
                  aria-label={t('player_shuffle', 'Aleatório')}
                  data-dica={t('player_shuffle', 'Aleatório')}
                  aria-pressed={store.shuffle}
                  onClick={store.toggleShuffle}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    shuffle
                  </span>
                </button>
              )}
              <button
                type="button"
                className="ap-icon ap-icon--step ds-resposta"
                aria-label={prevLabel}
                data-dica={prevLabel}
                onClick={prev}
              >
                <span className="material-symbols-rounded ap-fill" aria-hidden="true">
                  skip_previous
                </span>
              </button>
              {playButton}
              {nextButton}
              {talk ? (
                <button
                  type="button"
                  className="ap-icon ds-resposta"
                  aria-label={t('player_fwd30', 'Avançar 30 segundos')}
                  data-dica={t('player_fwd30', 'Avançar 30 segundos')}
                  onClick={() => seek(pos + 30)}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    forward_30
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="ap-icon ap-toggle ds-resposta"
                  aria-label={repeatLabel}
                  data-dica={repeatLabel}
                  aria-pressed={store.repeat !== 'off'}
                  onClick={store.cycleRepeat}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    {store.repeat === 'one' ? 'repeat_one' : 'repeat'}
                  </span>
                </button>
              )}
            </div>

            <div className="ap-row">
              <button
                type="button"
                className="ap-chip ds-resposta"
                aria-expanded={queueOpen}
                onClick={() => setQueueOpen((v) => !v)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  queue_music
                </span>
                {t('player_queue', 'Fila')}
                <span className="ap-chip__count">{ups.length}</span>
              </button>
              {talk ? (
                <button
                  type="button"
                  className="ap-chip ds-resposta"
                  aria-label={t('player_speed', 'Velocidade {{speed}}', { speed: speedText })}
                  onClick={store.cycleSpeed}
                >
                  {speedText}
                </button>
              ) : null}
              <span role="status" className="ap-ell ap-status">
                {status}
              </span>
              <button
                type="button"
                className="ap-icon ds-resposta"
                aria-label={t('share', 'Compartilhar')}
                data-dica={t('share', 'Compartilhar')}
                onClick={share}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  share
                </span>
              </button>
            </div>

            {queueOpen ? (
              <div className="ap-queue">
                <div className="ap-queue__title">{t('player_up_next_title', 'A seguir')}</div>
                {ups.length > 0 ? (
                  <ol>
                    {ups.map((i, j) => {
                      const q = store.ctx?.tracks[i];
                      if (!q) return null;
                      return (
                        <li key={`${q.id}-${j}`}>
                          <button
                            type="button"
                            className="ds-resposta"
                            aria-label={t('player_play_now', 'Tocar agora: {{title}}', { title: q.title })}
                            onClick={() => store.jump(i)}
                          >
                            <span className="ap-queue__n">{j + 1}</span>
                            {q.img ? <img src={q.img} alt="" /> : null}
                            <span className="ap-queue__text">
                              <span className="ap-ell">{q.title}</span>
                              {q.duration ? <span>{formatDuration(q.duration, talk)}</span> : null}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <div className="ap-queue__empty">
                    <span>{t('player_queue_empty', 'Não há mais nada na fila.')}</span>
                    <button
                      type="button"
                      className="ap-chip ds-resposta"
                      onClick={() => {
                        if (store.source && store.ctx) store.start(store.source, store.ctx, null);
                      }}
                    >
                      {t('player_play_again', 'Tocar de novo')}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </>
      )}
    </section>,
    document.body,
  );
}
